/**
 * A studio form parked in sessionStorage across the Stripe round trip, so a
 * visitor who pays for credits comes back to the same photos, style and note
 * and the render can start without re-entering anything.
 */
const KEY_PREFIX = 'ai-tool-draft:';
const MAX_AGE_MS = 60 * 60 * 1000;

type StoredDraft = { savedAt: number; draft: unknown };

const keyFor = (slug: string) => `${KEY_PREFIX}${slug}`;

export function saveStudioDraft(slug: string, draft: unknown): void {
  try {
    const stored: StoredDraft = { savedAt: Date.now(), draft };
    window.sessionStorage.setItem(keyFor(slug), JSON.stringify(stored));
  } catch {
    // Private mode or full storage: the visitor just refills the form.
  }
}

/** The parked draft, if fresh. Does not remove it; call `clearStudioDraft` once used. */
export function peekStudioDraft<T>(slug: string): T | null {
  try {
    const raw = window.sessionStorage.getItem(keyFor(slug));
    if (!raw) return null;
    const stored = JSON.parse(raw) as StoredDraft;
    if (Date.now() - stored.savedAt > MAX_AGE_MS) return null;
    return stored.draft as T;
  } catch {
    return null;
  }
}

export function clearStudioDraft(slug: string): void {
  try {
    window.sessionStorage.removeItem(keyFor(slug));
  } catch {
    // Nothing to clear.
  }
}
